import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useTheme, Fab, Stack, Box, IconButton, Typography, Grid, Modal, Button, Container, CircularProgress, Tooltip } from '@mui/material';
import { DataGridPro, DataGridProProps, GridColDef, GridRenderCellParams, GridToolbar, GridInitialState, useGridApiRef, gridRowCountSelector } from '@mui/x-data-grid-pro';
import { Add as AddIcon, Cached as CachedIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAmaWorkflowContext } from '../../../../context/ama-workflow-provider';
import { useDynamicGridHeight } from '../../../../hooks/useDynamicGridHeight';
import ClientsListRowTemplate from '../clients-list-row-template';
import { useSearchParams } from 'react-router-dom';
import { EditMode } from '../../../../data/enums';
import { localStorageKeys, urlParams } from '../../../../data/constants';

const GRID_VERT_OFFSET = 300;

interface IClientsListProps {
  mode: EditMode;
  setEditMode: React.Dispatch<React.SetStateAction<EditMode>>;
};

export default function ClientsList(props: IClientsListProps) {
  
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const [params, setParams] = useSearchParams();  
  const getDetailPanelContent = React.useCallback<NonNullable<DataGridProProps['getDetailPanelContent']>>(({ row }) => <DetailPanelContent row={row} />, []);
  const getDetailPanelHeight = React.useCallback(() => 'auto', []);
  const { clients, setSelectedClient, clientsLastUpdated, clientsLoading, loadClients, deleteClient } = useAmaWorkflowContext();
  const [idForDelete, setIdForDelete] = useState<string | null>(null);
  const { gridHeight } = useDynamicGridHeight({ gridItems: clients, offset: GRID_VERT_OFFSET });

  useLayoutEffect(() => {

    // Save grid state when view unmounts
    return () => {
      if (apiRef.current && apiRef.current.exportState) {
        console.log(`ClientsList.useLayoutEffect :: saving grid state to local storage`);
        localStorage.setItem(
          localStorageKeys.AMA_CLIENTS_GRID_STATE, 
          JSON.stringify(apiRef.current.exportState()));
      }
    };
  }, []);

  useLayoutEffect(() => {

    const selectedId = params.get(urlParams.ID);
    if (!selectedId || !clients || !apiRef.current) return;

    // Working around DataGridPro bug related to scrollToIndexes
    setTimeout(() => {
      try {

        console.log(`ClientsList :: scrolling to selected item`);
        const rowIndex = clients.findIndex(client => client.ClientId === selectedId);
        if (rowIndex >= 0) apiRef.current?.scrollToIndexes({ rowIndex: rowIndex, colIndex: 0 }); 
      
      } catch (error) {
        console.error(`ClientsList :: error scrolling to selected item: ${error}`);
      }
    }, 500);

  }, [clients]);

  const getInitialGridState = () : GridInitialState => {
    
    // Check for saved grid filters
    const gridStateStr = localStorage.getItem(localStorageKeys.AMA_CLIENTS_GRID_STATE);
    
    // Init initial grid state obj
    const gridState = gridStateStr ? JSON.parse(gridStateStr) : {};

    // Check querystring for selected row and update grid state
    const selectedId = params.get(urlParams.ID);
    if (selectedId) {
      const detailPanel = { 'expandedRowIds': [selectedId] };
      gridState.detailPanel = detailPanel;
    }

    // TODO - Check querystring for grid filter and update grid state
    const qsFilterStr = params.get(urlParams.AMA_GRID_FILTER);   

    // Return initial grid state
    return gridState;
  };

  const handleEditButtonClick = (id: string) => {
    setSelectedClient(id);   
    props.setEditMode(EditMode.Edit);
  };

  const handleDeleteButtonClick = async (id: string) => {
    setIdForDelete(id);
  };

  const handleAddButtonClick = () => {
    props.setEditMode(EditMode.Add);
  };

  const handleRefreshButtonClick = async () => {
    await loadClients();
  };

  const handleModalOkButtonClick = async () => {
    if (idForDelete) {
      const id = idForDelete;
      setIdForDelete(null);
      await deleteClient(id);
    }
  };

  const handleModalCancelButtonClick = () => {
    setIdForDelete(null);
  };

  const CustomFooter = () => {

    return (
      <Container sx={{ height:  40 }}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ height: '100%', color: theme.palette.text.secondary }}>
          <Grid item xs={4}>
            <Typography variant="body2" align="left">
              Records: {clients?.length}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography variant="body2" align="center" sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.secondary }}>
            <a href="#"
              aria-label="Refresh Client List"
              aria-describedby='client_refresh_timestamp'
              title='Refresh Client List'
              onClick={(e) => { e.preventDefault(); handleRefreshButtonClick();}}>
             <CachedIcon 
                sx={{ 
                  verticalAlign: 'middle', 
                  mr: 1, mb:.3, 
                  color: theme.palette.text.disabled,
                  animation: clientsLoading ? 'spin 1s linear infinite' : 'none', 
                }} />            
            </a>
            <span id="client_refresh_timestamp"><span className='visually-hidden'>Last Refreshed: </span>{clientsLastUpdated}</span>
            </Typography>
          </Grid>
          <Grid item xs={4} md={4}>
            &nbsp;
          </Grid>
        </Grid>
      </Container>
    );
  };

  const DetailPanelContent = ({ row }: { row: IClient }) => {
    return (
      <ClientsListRowTemplate 
        client={row} />
    );
  };

  const RenderEditColumn = (client: IClient) => {
    if (!client) return <></>
    return (
      <Tooltip title="Edit client record.">
        <IconButton 
          aria-label="Edit client record."
          sx={{ color: theme.palette.grey[600]}}
          onClick={() => 
            { 
              handleEditButtonClick(client.ClientId);
            }}>
          <EditIcon />
        </IconButton>
      </Tooltip>
    )
  };
    
  const RenderDeleteColumn = (client: IClient) => {
    if (!client) return <></>
    const isHideAdminUI = process.env.REACT_APP_HIDE_ADMIN_UI === 'true';
    return (
      <Tooltip 
        title="Delete client record."
        {...(isHideAdminUI ? { className: 'admin-ui' } : {})}>
        <IconButton 
          aria-label="Delete client record."
          sx={{ color: theme.palette.grey[600]}}
          onClick={() => 
            { 
              handleDeleteButtonClick(client.ClientId)
            }}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    )
  };

  const columns: Array<GridColDef> = [
    { 
      field: 'ClientId', type: 'string', 
      headerName: 'Client ID', flex: .3, minWidth: 200,
      resizable: true, sortable: true
    },
    { 
      field: 'DisplayName', type: 'string', 
      headerName: 'Full Name', flex: .7,
      resizable: true, sortable: true
    },
    { 
      field: 'Delete', type: 'string', 
      headerName: process.env.REACT_APP_HIDE_ADMIN_UI === 'true' ? '' : 'Delete', 
      minWidth: 75, maxWidth: 100, headerAlign: 'center', align: 'center',
      resizable: true, sortable: false,
      renderCell: (params: GridRenderCellParams<any, Date>) => (<> { RenderDeleteColumn(params.row as IClient) } </>)      
    },
    { 
      field: 'IsActive', type: 'boolean', 
      headerName: 'Active', minWidth: 75, maxWidth: 100, headerAlign: 'center', align: 'center',
      resizable: false, sortable: true
    },
    { 
      field: 'Edit', type: 'string', 
      headerName: 'Edit', minWidth: 75, maxWidth: 100, headerAlign: 'center', align: 'center',
      resizable: true, sortable: false,
      renderCell: (params: GridRenderCellParams<any, Date>) => (<> { RenderEditColumn(params.row as IClient) } </>)
    }
  ];

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
          
      {/* Loading overlay */}
      {clientsLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.6)',
            zIndex: 999,
          }}>
          <CircularProgress />
        </div>
      )}

      {/* Grid */}
      {clients &&

        <DataGridPro
          columns={columns}
          rows={wrapClients(clients)}
          rowThreshold={0}
          isRowSelectable={() => false}
          getDetailPanelHeight={getDetailPanelHeight}
          getDetailPanelContent={getDetailPanelContent}
          apiRef={apiRef}
          initialState={getInitialGridState()}          
          //disableColumnMenu={true}
          disableDensitySelector={true}
          disableColumnResize={false}
          //disableColumnFilter={true}
          disableColumnSelector={true}
          slots={{ toolbar: GridToolbar, footer: () => (
            <Stack justifyContent="center" alignItems="center">
              <CustomFooter />
            </Stack>
          ) }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            
              printOptions: { disableToolbarButton: true },
              csvOptions: {disableToolbarButton: true}                
          }}}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
          }            
          sx={{
            height: gridHeight,
            minHeight: '300px',
            '& .MuiDataGrid-root': {
              color: theme => theme.palette.text.primary,
              fontSize: '0.9rem',
              maxHeight: '100%'
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: theme => theme.palette.background.paper,
              color: theme => theme.palette.text.primary,
              fontSize: '.9rem'
            },
            '& .MuiDataGrid-cell': {
              color: theme => theme.palette.text.primary,
              fontSize: '0.9rem'
            },
            '& .MuiDataGrid-row.Mui-even .MuiDataGrid-cell': {
              backgroundColor: theme => theme.palette.grey[100]
            },
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
              backgroundColor: theme => theme.palette.grey[300]
            }
          }}
        />
      }
      
      {/* Add Client button */}
      <Fab color="primary" aria-label="add"        
        sx={{ position: "absolute", bottom: '10px', right: '65px' }} onClick={() => handleAddButtonClick()}>
        <AddIcon />
      </Fab>

      {/* Delete confirmation modal */}
      <Modal
        open={Boolean(idForDelete)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={{
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.grey[300]}`,
          boxShadow: 24,
          p: 4,}}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Delete
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 4 }}>
            Are you sure you want to delete {idForDelete}?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" color="primary" sx={{mr: 2}} onClick={handleModalOkButtonClick}>Ok</Button>
            <Button variant="outlined" color="primary" onClick={handleModalCancelButtonClick}>Cancel</Button>
          </Box>
        </Box>
      </Modal>

    </Box>
  );
}

const wrapClients = (clients: Array<IClient>) => {  
  return clients.map((jsonObject) => {
    const newObj = { ...jsonObject, id: jsonObject.ClientId };  
    return newObj;
  });
};	


// strategy
// mount - call initializeGridState - return json object to init grid
//   - check for local storage grid filter
//   - check for QS params
//     - id - selected row
//     - f - grid filter
// event - grid change state
//   - save grid filters to local storage
//   - update QS params - expanded row

  // const clearIdParameter = () => {  
  //   params.delete(urlParams.ID);
  //   setParams(params);
  //   saveGridState();
  // };

  // const setUrlParameters = () => {
  //   const currentState = encodeURIComponent(JSON.stringify(apiRef?.current?.exportState().filter));
  //   setParams({[urlParams.AMA_GRID_FILTER]: currentState});
  // };


  // TODO: grid filter record count

  // useEffect(() => {    
  //   // if (!apiRef.current) return;  
  //   if (params.get(urlParams.ID)) {        
  //     scrollToRow(params.get(urlParams.ID) ?? '', true, 0);     
  //   }
  // }, [apiRef]);

  // useEffect(() => {
  //   const id = params.get(urlParams.ID);
  //   console.log(`ClientsList.useEffect[apiRef] :: id: ${id}`);
  //   if (id) scrollToRow(id, true, 0);
  // }, [apiRef]);

  // const scrollToRow = (client: string, clearLocation: boolean, attempt: number) => { // TODO: In the future, change this to not need settimeout.
  //   // if we're using setTimeout, should be a clear timeout
  //   // if (!apiRef.current.getRowIndexRelativeToVisibleRows) {    
  //   if (!apiRef.current || !apiRef.current.getRowIndexRelativeToVisibleRows) {   
  //     console.log(`ClientsList.scrollToRow :: add delay`); 
  //     if (attempt < 20) setTimeout(function() { scrollToRow(client, clearLocation, attempt + 1); }, 250);
  //   } else {
  //     const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(client);
  //     console.log(`ClientsList.scrollToRow :: rowIndex: ${rowIndex}`); 
  //     if (rowIndex >= 0) apiRef.current.scrollToIndexes({ rowIndex: rowIndex, colIndex: 0 }); 
  //     if (clearLocation) clearIdParameter(); 
  //   }
  // };

  // const initializeGridState = (selectedId: string | null, storageKey: string) => {
  //   // if (viewId != null && viewId != '') return {detailPanel: { expandedRowIds: [viewId]}}; 
  //   if (selectedId) {
  //     return { detailPanel: { expandedRowIds: [selectedId]} }; 
  //   } else {
  //     const stateParams = params.get(urlParams.AMA_GRID_FILTER);
  //     // const stateParamsArray = stateParams && stateParams != '' ? JSON.parse(decodeURIComponent(stateParams ?? '')) : [];
  //     const stateParamsArray = stateParams ? JSON.parse(decodeURIComponent(stateParams)) : [];
  //     if (stateParamsArray.length > 0) return { filter: stateParamsArray };
  //     else {
  //       const stateFromLocalStorage = localStorage?.getItem(storageKey);
  //       if (stateFromLocalStorage) return JSON.parse(stateFromLocalStorage);
  //       // if (stateFromLocalStorage && stateFromLocalStorage != '') return JSON.parse(stateFromLocalStorage);
  //     }
  //     return {};
  //   }    
  // }; 


  // const state: GridInitialState = initializeView(
  //   params.get(urlParams.ID) ?? '', 
  //   localStorageKeys.AMA_CLIENTS_GRID_STATE);  
  
  // const initialGridState: GridInitialState = initializeGridState(
  //   params.get(urlParams.ID), 
  //   localStorageKeys.AMA_CLIENTS_GRID_STATE);  

  // const saveGridState = () => {
  //   if (!apiRef.current) return;
  //   if (apiRef.current.exportState && localStorage) {
  //     const currentState = JSON.stringify(apiRef.current.exportState());
  //     localStorage.setItem(localStorageKeys.AMA_CLIENTS_GRID_STATE, currentState);  
  //   }
  // };

  // const saveGridState = () => {
  //   if (!apiRef.current || !apiRef.current.exportState || !localStorage) return;
  //   console.log(`ClientsList.saveGridState :: ${JSON.stringify(apiRef.current.exportState())}`)
  //   localStorage.setItem(
  //     localStorageKeys.AMA_CLIENTS_GRID_STATE, 
  //     JSON.stringify(apiRef.current.exportState()));
  // };

  // onStateChange={handleGridStateChange}
  // const handleGridStateChange = (state: GridState) => {
  //   const expandedRowIds = state.detailPanel.expandedRowIds;
  //   const lastItem  = expandedRowIds.length > 0 ? expandedRowIds[expandedRowIds.length - 1].toString() : null;
    
  //   // const searchParams = new URLSearchParams(location.search);
  //   // Object.entries(params).forEach(([key, value]) => {
  //   //   if (value === null) {
  //   //     searchParams.delete(key);
  //   //   } else {
  //   //     searchParams.set(key, value);
  //   //   }
  //   // });
  //   // if (lastItem !== urlParams.ID) {
  //   //   if (lastItem) {
  //   //     setParams({[urlParams.ID]: lastItem});
  //   //   } else {
  //   //     setParams({});
  //   //   }
  //   // }
  //   // if (lastItem) setParams({[urlParams.ID]: lastItem});
  //   // else setParams({});
  //   // navigate({ ...location, search: searchParams.toString() }, { replace: true });
  //   console.log(`ClientsList.handleGridStateChange :: lastItem: ${lastItem}`);
  // };

  // const [visibleRows, setVisibleRows] = React.useState<number>(0);

  // React.useEffect(() => {
  //   // stateChange, pageChange, pageSizeChange, filterModelChange
  //   apiRef.current.subscribeEvent("filterModelChange", () => {
  //     const count = gridRowCountSelector(apiRef.current.state);
  //     setVisibleRows(count);
  //   });
  // }, [apiRef]);